import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  clearStoredUserInfo,
  emptyProfile,
  isCanonicalProfileChromeFor,
  loadStoredUserInfo,
  persistUserInfo,
  PROFILE_CHROME_SOURCE_KK_PROFILE,
  resolveCanonicalChromeAvatar,
  syncProfileForAuthenticatedUser,
} from '../auth/profileIdentity';
import { fetchCanonicalProfileChrome } from '../auth/canonicalProfileChrome';
import { loadImpersonationSession } from '../supportMode/supportModeSession';
import { bindChromeToImpersonationSession } from '../supportMode/impersonationIdentity';

const UserContext = createContext();

/**
 * Canonical self-profile hydration status (direct login only).
 *   idle    — not started (unauthenticated, or Support Mode → 'skipped')
 *   pending — GET /me/profile in flight for `sub`
 *   ready   — chrome came from kk-profile for `sub`
 *   error   — read failed; Auth0/initials fallback stays in effect
 *   skipped — Support Mode: selected-user chrome is owned by the shell
 */
const IDLE_HYDRATION = Object.freeze({ status: 'idle', sub: null });

function readAuthUser() {
  try {
    return JSON.parse(localStorage.getItem('auth_user') || 'null');
  } catch {
    return null;
  }
}

function hydrateFromAuth() {
  const authUser = readAuthUser();
  const authSub = authUser?.sub ? String(authUser.sub) : null;
  if (!authSub) {
    return { profile: emptyProfile(), ready: true, authenticated: false };
  }
  const stored = loadStoredUserInfo(authSub);
  const synced = syncProfileForAuthenticatedUser(authUser, stored);
  return { profile: synced, ready: true, authenticated: true };
}

export const UserProvider = ({ children }) => {
  // Hydrate synchronously from auth_user so header chrome never flashes
  // empty-identity "??" while the first effect runs. Scoped to Auth0 sub.
  const [userInfo, setUserInfo] = useState(() => hydrateFromAuth().profile);
  const [identityReady, setIdentityReady] = useState(
    () => hydrateFromAuth().ready,
  );
  const [canonicalProfile, setCanonicalProfile] = useState(IDLE_HYDRATION);
  // Latest hydration state + in-flight promise (dedupe per sub, no loops).
  const hydrationRef = useRef({ ...IDLE_HYDRATION, promise: null });
  const userInfoRef = useRef(userInfo);
  userInfoRef.current = userInfo;

  /**
   * Re-sync chrome from auth_user (mount, storage events, token refresh).
   * If the in-memory chrome is already canonical kk-profile chrome for the
   * SAME authenticated sub, keep it — kk-profile is SoR over Auth0 claims and
   * its derived avatarUrl lives in memory only. A different sub always wins.
   */
  const applyAuthSync = useCallback((authUser) => {
    const authSub = authUser?.sub ? String(authUser.sub) : null;
    const stored = loadStoredUserInfo(authSub);
    const synced = syncProfileForAuthenticatedUser(authUser, stored);
    if (isCanonicalProfileChromeFor(userInfoRef.current, synced.sub)) {
      setIdentityReady(true);
      return;
    }
    setUserInfo(synced);
    persistUserInfo(synced, synced.sub);
    setIdentityReady(true);
  }, []);

  const setHydration = useCallback((next) => {
    hydrationRef.current = { ...hydrationRef.current, ...next };
    setCanonicalProfile({ status: next.status, sub: next.sub ?? null });
  }, []);

  const updateUserInfo = useCallback((info) => {
    if (!info) {
      setUserInfo(emptyProfile());
      clearStoredUserInfo();
      setIdentityReady(true);
      return;
    }

    const authUser = readAuthUser();
    // Support Mode: chrome updates come from canonical reads made under the
    // impersonation JWT (selected user). Bind them to the selected user's sub
    // so they never persist under — or render for — the operator's identity.
    const bound = bindChromeToImpersonationSession(
      info,
      loadImpersonationSession(),
    );
    const sub = bound.sub || authUser?.sub || null;
    const next = {
      ...emptyProfile(),
      ...bound,
      companyAvatar: bound.companyAvatar || null,
      avatar: bound.avatar || null,
      sub: sub ? String(sub) : null,
    };
    if (next.profileSource === PROFILE_CHROME_SOURCE_KK_PROFILE) {
      Object.assign(next, resolveCanonicalChromeAvatar(next, authUser));
    }
    setUserInfo(next);
    persistUserInfo(next, next.sub);
    setIdentityReady(true);
  }, []);

  /**
   * Canonical self hydration: GET /me/profile → shared chrome (avatar, name).
   * Direct login only — NEVER runs while a Support Mode session is active
   * (the shell owns the selected user's chrome). Deduped per authenticated
   * sub; `force` performs one deliberate re-read (signed-URL recovery).
   * Resolves to the chrome, or null when skipped/failed.
   */
  const hydrateCanonicalProfile = useCallback(
    async ({ force = false } = {}) => {
      const authUser = readAuthUser();
      const sub = authUser?.sub ? String(authUser.sub) : null;
      if (!sub) return null;
      if (loadImpersonationSession()) {
        setHydration({ status: 'skipped', sub, promise: null });
        return null;
      }
      const current = hydrationRef.current;
      if (current.sub === sub && current.status === 'pending' && current.promise) {
        return current.promise;
      }
      if (!force && current.sub === sub && current.status === 'ready') {
        return null;
      }
      const promise = (async () => {
        try {
          const chrome = await fetchCanonicalProfileChrome();
          // Session changed mid-flight (logout / other user) → discard.
          const nowSub = readAuthUser()?.sub ? String(readAuthUser().sub) : null;
          if (nowSub !== sub || loadImpersonationSession()) {
            setHydration({ status: 'idle', sub: null, promise: null });
            return null;
          }
          updateUserInfo(chrome);
          setHydration({ status: 'ready', sub, promise: null });
          return chrome;
        } catch {
          // Fail soft: Auth0 picture / initials fallback remains in effect.
          setHydration({ status: 'error', sub, promise: null });
          return null;
        }
      })();
      setHydration({ status: 'pending', sub, promise });
      return promise;
    },
    [setHydration, updateUserInfo],
  );

  useEffect(() => {
    const authUser = readAuthUser();
    const authSub = authUser?.sub ? String(authUser.sub) : null;

    if (authSub) {
      applyAuthSync(authUser);
      return;
    }

    const stored = loadStoredUserInfo(authSub);
    if (stored?.email || stored?.firstName) {
      clearStoredUserInfo();
    }
    setUserInfo(emptyProfile());
    setIdentityReady(true);
  }, [applyAuthSync]);

  useEffect(() => {
    const syncFromAuth = () => {
      const authUser = readAuthUser();
      if (!authUser?.sub) {
        setUserInfo(emptyProfile());
        clearStoredUserInfo();
        setIdentityReady(true);
        hydrationRef.current = { ...IDLE_HYDRATION, promise: null };
        setCanonicalProfile(IDLE_HYDRATION);
        return;
      }
      applyAuthSync(authUser);
    };
    const onStorage = (event) => {
      if (event.key !== 'auth_user' && event.key !== 'userInfo' && event.key !== null) {
        return;
      }
      syncFromAuth();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('kk-auth-session-changed', syncFromAuth);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('kk-auth-session-changed', syncFromAuth);
    };
  }, [applyAuthSync]);

  return (
    <UserContext.Provider
      value={{
        userInfo,
        updateUserInfo,
        identityReady,
        canonicalProfile,
        hydrateCanonicalProfile,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
